import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useHistory } from 'react-router-dom';
import API_BASE_URL from '../config';
import { ChevronLeft, Plus, Edit2, Trash2, Save, X, Utensils, Globe, QrCode as QrIcon, Download } from 'lucide-react';
import { QRCodeCanvas } from 'qrcode.react';
import { jsPDF } from 'jspdf';
import './FoodManagement.css';

interface FoodItem {
    name: string;
    description: string;
    isAvailable: boolean;
}

interface FoodCounter {
    _id: string;
    name: string;
    slug: string;
    description: string;
    items: FoodItem[];
    isActive: boolean;
}

const FoodManagement: React.FC = () => {
    const { accessToken } = useAuth();
    const history = useHistory();
    const [counters, setCounters] = useState<FoodCounter[]>([]);
    const [loading, setLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingCounter, setEditingCounter] = useState<Partial<FoodCounter> | null>(null);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [qrModalOpen, setQrModalOpen] = useState(false);
    const [qrCounter, setQrCounter] = useState<FoodCounter | null>(null);

    const downloadPDF = () => {
        if (!qrCounter) return;
        const doc = new jsPDF();
        const canvas = document.getElementById('stall-qr-canvas') as HTMLCanvasElement;
        if (!canvas) return;

        const qrImage = canvas.toDataURL('image/png');

        // PDF Layout
        doc.setFontSize(22);
        doc.setTextColor(255, 77, 77); // Primary color
        doc.text(qrCounter.name, 105, 40, { align: 'center' });

        doc.setFontSize(14);
        doc.setTextColor(100, 116, 139); // Muted color
        doc.text('Scan to View Menu & Order Digitally', 105, 50, { align: 'center' });

        doc.addImage(qrImage, 'PNG', 55, 70, 100, 100);

        doc.setFontSize(10);
        doc.text(`Stall ID: ${qrCounter.slug}`, 105, 180, { align: 'center' });
        doc.text('Wedding Management System', 105, 200, { align: 'center' });

        doc.save(`${qrCounter.slug}-qr.pdf`);
    };

    const fetchCounters = async () => {
        try {
            const res = await fetch(`${API_BASE_URL}/api/food-counters/admin/all`, {
                headers: { Authorization: `Bearer ${accessToken}` }
            });
            const data = await res.json();
            if (res.ok) {
                setCounters(data);
            } else {
                setError('Failed to load counters');
            }
        } catch (err) {
            console.error('Error fetching counters:', err);
            setError('Connection error');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchCounters();
    }, [accessToken]);

    const handleSaveCounter = async (e: React.FormEvent) => {
        e.preventDefault();
        setSaving(true);
        setError(null);

        const isNew = !editingCounter?._id;
        const method = isNew ? 'POST' : 'PUT';
        const url = isNew
            ? `${API_BASE_URL}/api/food-counters`
            : `${API_BASE_URL}/api/food-counters/${editingCounter._id}`;

        // Clean up data to ensure it matches schema and doesn't send unwanted fields
        const payload = {
            name: editingCounter?.name,
            slug: editingCounter?.slug,
            description: editingCounter?.description || '',
            items: editingCounter?.items || [],
            isActive: editingCounter?.isActive !== undefined ? editingCounter.isActive : true
        };

        console.log('Saving counter with payload:', payload);

        try {
            const res = await fetch(url, {
                method,
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${accessToken}`
                },
                body: JSON.stringify(payload)
            });
            const data = await res.json();

            if (res.ok) {
                setIsModalOpen(false);
                fetchCounters();
            } else {
                setError(data.message || 'Error saving stall');
                alert(`Error: ${data.message || 'Unknown error occurred while saving.'}`);
            }
        } catch (err) {
            console.error('Error saving counter:', err);
            setError('Server connection failed');
            alert('Failed to connect to the server. Please try again.');
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (id: string) => {
        if (!window.confirm('Are you sure you want to delete this stall? This action cannot be undone.')) return;
        try {
            const res = await fetch(`${API_BASE_URL}/api/food-counters/${id}`, {
                method: 'DELETE',
                headers: { Authorization: `Bearer ${accessToken}` }
            });
            if (res.ok) {
                fetchCounters();
            } else {
                alert('Failed to delete stall');
            }
        } catch (err) {
            console.error('Error deleting counter:', err);
        }
    };

    const addItem = () => {
        const items = [...(editingCounter?.items || []), { name: '', description: '', isAvailable: true }];
        setEditingCounter({ ...editingCounter, items });
    };

    const updateItem = (index: number, field: keyof FoodItem, value: any) => {
        const items = [...(editingCounter?.items || [])];
        items[index] = { ...items[index], [field]: value };
        setEditingCounter({ ...editingCounter, items });
    };

    const removeItem = (index: number) => {
        const items = [...(editingCounter?.items || [])];
        items.splice(index, 1);
        setEditingCounter({ ...editingCounter, items });
    };

    if (loading) return (
        <div className="loading-container">
            <Utensils size={40} className="animate-bounce mb-4" />
            <p>Loading your food magic...</p>
        </div>
    );

    return (
        <div className="food-management-page">
            <header className="food-mgmt-header">
                <div className="header-left">
                    <button className="back-btn" onClick={() => history.push('/admin/dashboard')}>
                        <ChevronLeft size={20} />
                    </button>
                    <h1>Catering & Food Stalls</h1>
                </div>
                <button className="add-btn" onClick={() => {
                    setEditingCounter({ name: '', slug: '', items: [], isActive: true });
                    setIsModalOpen(true);
                }}>
                    <Plus size={20} /> Add New Stall
                </button>
            </header>

            {error && <div className="error-pill" style={{ background: '#fef2f2', color: '#dc2626', padding: '0.75rem', borderRadius: '12px', marginBottom: '1.5rem', fontWeight: 600, fontSize: '0.85rem', textAlign: 'center' }}>⚠️ {error}</div>}

            <div className="counters-grid">
                {counters.map(counter => (
                    <div key={counter._id} className="counter-card">
                        <div className="card-top">
                            <h3>{counter.name}</h3>
                            <span className={`status-badge ${counter.isActive ? 'active' : 'inactive'}`}>
                                {counter.isActive ? 'Live' : 'Hidden'}
                            </span>
                        </div>
                        <div className="slug-text">/{counter.slug}</div>
                        <div className="items-preview">
                            {counter.items.length > 0 ? (
                                counter.items.map((item, i) => (
                                    <span key={i} className="item-tag">{item.name}</span>
                                ))
                            ) : (
                                <span className="text-gray-400 text-xs italic">No items yet</span>
                            )}
                        </div>
                        <div className="card-actions">
                            <button className="edit-btn" onClick={() => { setEditingCounter(counter); setIsModalOpen(true); }}>
                                <Edit2 size={16} /> Edit
                            </button>
                            <button className="qr-btn" onClick={() => { setQrCounter(counter); setQrModalOpen(true); }} style={{ background: '#f8fafc', color: 'var(--text-main)' }}>
                                <QrIcon size={16} /> Print QR
                            </button>
                            <button className="delete-btn" onClick={() => handleDelete(counter._id)}>
                                <Trash2 size={16} /> Delete
                            </button>
                        </div>
                    </div>
                ))}
            </div>

            {qrModalOpen && qrCounter && (
                <div className="modal-overlay" onClick={() => setQrModalOpen(false)}>
                    <div className="mgmt-modal qr-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '400px', textAlign: 'center' }}>
                        <div className="modal-header">
                            <h2>Stall QR Code</h2>
                            <p style={{ fontSize: '0.9rem', color: 'var(--text-muted)' }}>{qrCounter.name}</p>
                        </div>

                        <div style={{ background: 'white', padding: '2rem', borderRadius: '24px', display: 'inline-block', margin: '1rem 0', border: '1px solid #edf2f7' }}>
                            <QRCodeCanvas
                                id="stall-qr-canvas"
                                value={`${process.env.REACT_APP_ORDER_MANAGER_URL || 'http://localhost:3000'}/order/${qrCounter.slug}`}
                                size={200}
                                level="H"
                                includeMargin={true}
                            />
                        </div>

                        <div style={{ marginTop: '1.5rem', display: 'flex', gap: '1rem' }}>
                            <button className="cancel-btn" onClick={() => setQrModalOpen(false)}>Close</button>
                            <button className="save-btn-premium" onClick={downloadPDF}>
                                <Download size={18} style={{ display: 'inline', marginRight: '8px' }} />
                                Download PDF
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {isModalOpen && (
                <div className="modal-overlay" onClick={() => !saving && setIsModalOpen(false)}>
                    <div className="mgmt-modal" onClick={e => e.stopPropagation()}>
                        <div className="modal-header">
                            <h2>{editingCounter?._id ? 'Edit Stall' : 'New Food Stall'}</h2>
                        </div>

                        <form onSubmit={handleSaveCounter}>
                            <div className="form-group">
                                <label>Stall Name</label>
                                <input
                                    value={editingCounter?.name || ''}
                                    onChange={e => {
                                        const name = e.target.value;
                                        setEditingCounter({
                                            ...editingCounter,
                                            name,
                                            slug: editingCounter?._id ? editingCounter.slug : name.toLowerCase().trim().replace(/[^a-z0-9]+/g, '-')
                                        });
                                    }}
                                    placeholder="e.g. Italian Pasta Station"
                                    required
                                    disabled={saving}
                                />
                            </div>

                            <div className="form-group">
                                <label>
                                    <Globe size={14} style={{ display: 'inline', marginRight: '4px' }} />
                                    URL Slug (unique)
                                </label>
                                <input
                                    value={editingCounter?.slug || ''}
                                    onChange={e => setEditingCounter({ ...editingCounter, slug: e.target.value.toLowerCase().replace(/[^a-z0-9]+/g, '-') })}
                                    placeholder="italian-pasta"
                                    required
                                    disabled={saving}
                                />
                            </div>

                            <div className="items-section">
                                <div className="section-title-row">
                                    <label>Menu Items ({editingCounter?.items?.length || 0})</label>
                                    <button type="button" className="add-item-mini" onClick={addItem} disabled={saving}>
                                        <Plus size={14} style={{ display: 'inline' }} /> Add Item
                                    </button>
                                </div>

                                <div style={{ maxHeight: '350px', overflowY: 'auto', paddingRight: '5px' }}>
                                    {editingCounter?.items?.map((item, idx) => (
                                        <div key={idx} className="item-row">
                                            <div className="item-row-inputs">
                                                <input
                                                    value={item.name}
                                                    onChange={e => updateItem(idx, 'name', e.target.value)}
                                                    placeholder="Item Name (e.g. Pasta)"
                                                    required
                                                    disabled={saving}
                                                />
                                                <input
                                                    value={item.description}
                                                    onChange={e => updateItem(idx, 'description', e.target.value)}
                                                    placeholder="Short description"
                                                    disabled={saving}
                                                />
                                            </div>
                                            <div className="item-row-actions">
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                    <div
                                                        className={`custom-checkbox ${item.isAvailable ? 'checked' : ''}`}
                                                        onClick={() => !saving && updateItem(idx, 'isAvailable', !item.isAvailable)}
                                                        style={{ width: '32px', height: '18px', flexShrink: 0 }}
                                                    ></div>
                                                    <span style={{ fontSize: '0.65rem', fontWeight: 700 }}>Avail.</span>
                                                </div>
                                                <button
                                                    type="button"
                                                    className="qr-mini-btn"
                                                    onClick={() => {
                                                        const itemStall = { ...editingCounter, name: `${editingCounter.name} - ${item.name}`, slug: `${editingCounter.slug}?item=${encodeURIComponent(item.name)}` } as FoodCounter;
                                                        setQrCounter(itemStall);
                                                        setQrModalOpen(true);
                                                    }}
                                                    style={{ background: '#f1f5f9', border: 'none', borderRadius: '8px', padding: '6px', cursor: 'pointer' }}
                                                >
                                                    <QrIcon size={14} />
                                                </button>
                                                <button type="button" className="remove-row" onClick={() => removeItem(idx)} disabled={saving}>
                                                    <X size={16} />
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                </div>

                                {(!editingCounter?.items || editingCounter.items.length === 0) && (
                                    <div className="text-center p-4 text-slate-400 text-sm italic">
                                        Click "Add Item" to start your menu
                                    </div>
                                )}
                            </div>

                            <div className="toggle-group">
                                <div
                                    className={`custom-checkbox ${editingCounter?.isActive ? 'checked' : ''}`}
                                    onClick={() => !saving && setEditingCounter({ ...editingCounter, isActive: !editingCounter?.isActive })}
                                ></div>
                                <label>Counter is Active & Visible</label>
                            </div>

                            <div className="modal-footer">
                                <button type="button" className="cancel-btn" onClick={() => setIsModalOpen(false)} disabled={saving}>
                                    Cancel
                                </button>
                                <button type="submit" className="save-btn-premium" disabled={saving}>
                                    {saving ? 'Saving...' : <><Save size={18} style={{ display: 'inline', marginRight: '8px' }} /> Save Stall</>}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default FoodManagement;
